<template>
  <div class="autocomplete">
    <input
      :id="id"
      name="sensis-input"
      type="text"
      :class="{ 'border-red': hasError, classname }"
      :placeholder="placeholder"
      v-model="inputText"
      @input="checkAddress"
      @keydown.enter.prevent="keyEnter"
      @keydown.up.capture="keyUp"
      @keydown.down.capture="keyDown"
      @keydown.tab.prevent="keyEnter"
      @keydown.escape.prevent="keyEscape"
      v-on:click="clickInside()"
    />

    <!-- Loading when fetching address' details -->
    <div class="input-loading" v-if="fetchingDetails">
      <img
        alt="Loading while fetching address' detail"
        src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/2903/dist/assets/img/loading-tail-spin.svg"
      />
    </div>

    <div class="suggestions" v-if="displaySuggestions">
      <!-- LOADING -->
      <div class="suggestion-loading" v-if="isLoading">
        <img
          alt="Loading while fetching suggestions"
          src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/2903/dist/assets/img/loading-three-dots.svg"
        />
      </div>

      <!-- LIST OF SUGGESTIONS -->
      <div
        id="sensis-suggestion-list"
        class="suggestion-list"
        v-if="!isLoading && suggestions.length > 0"
      >
        <!-- List -->
        <div
          :id="'sensis-suggestion-item-' + p.index"
          class="suggestion-item"
          :class="{ 'item-active': cursor === p.index }"
          @click="selectAddress(p)"
          @mouseover="cursor = p.index"
          v-for="p in suggestions"
        >
          {{ p.formatted_address }}
        </div>
      </div>

      <div class="suggestion-list" v-if="!isLoading && suggestions.length == 0">
        <!-- Empty -->
        <div class="suggestion-item empty" @click="keyEscape">
          No Suggestions
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SensisAutocomplete",

  props: {
    classname: String,
    id: String,
    showNotFound: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      inputText: "",
      suggestions: [],
      cursor: 1,
      displaySuggestions: false,
      isLoading: false,
      timeout: null,
      delay: 600, // ms
      minInputLength: 4,
      lastTimestamp: Date.now(),
      fetchingDetails: false,
      isClean: false,
      hasError: false,
    };
  },

  methods: {
    clickInside() {
      if (this.inputText && this.inputText.length >= this.minInputLength) {
        this.displaySuggestions = true;
      }
    },
    checkAddress() {
      // Reset flags
      this.isClean = false;
      this.hasError = false;

      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      // In any case, if the input changed, the suggestions will change
      this.suggestions = [];

      if (this.inputText && this.inputText.length >= this.minInputLength) {
        this.isLoading = true;
        let timestamp = Date.now(); // timestamp for this api call
        this.lastTimestamp = timestamp; // 1 , 2 , 3
        this.timeout = setTimeout(() => {
          this.displaySuggestions = true;
          const input = this.inputText;

          // Fetch Suggestions
          this.$store
            .dispatch("parseAddress", input)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                if (response.status === "OK") {
                  this.suggestions = response.results;
                }

                // If we show the not found, add the options even if the result is not correct
                if (this.showNotFound) {
                  this.suggestions.push({
                    index: this.suggestions.length + 1,
                    formatted_address: "I can't find my address",
                    addressNotFound: true,
                  });
                }
                this.isLoading = false;
              }
            })
            .catch((err) => {
              this.isLoading = false;
              console.error(err);
            });
        }, this.delay);
      } else {
        this.displaySuggestions = false;
        this.isLoading = false;
      }
    },
    selectAddress(item) {
      this.inputText = item.formatted_address;
      this.displaySuggestions = false;
      this.isLoading = false;
      this.suggestions = [];
      this.fetchingDetails = true;

      if (item.addressNotFound) {
        this.fetchingDetails = false;
        this.isLoading = false;
        this.$emit("addressSelected", null, null, true);
        return;
      }

      this.$store
        .dispatch("getAddressDetails", item.search_result_id)
        .then((response) => {
          if (response.result) {
            // Format with First letter in uppercase to match our format
            const country =
              response.result.country.charAt(0) +
              response.result.country.slice(1).toLowerCase();

            const selectedAddress = {
              unitnumber: response.result.subpremise,
              streetno: response.result.street_number,
              streetname: `${response.result.street_name} ${response.result.street_type}`,
              postcode: response.result.postcode,
              state: response.result.state,
              suburb: response.result.suburb,
              country: country,
            };

            this.fetchingDetails = false;
            this.isClean = true;
            this.hasError = false;
            this.$emit(
              "addressSelected",
              selectedAddress,
              item.formatted_address
            );
          }
        })
        .catch((err) => {
          this.suggestions = [];
          this.isLoading = false;
          this.fetchingDetails = false;
          console.error(err);
        });
    },
    keyUp() {
      if (this.displaySuggestions) {
        event.preventDefault();
        if (this.cursor > 1) {
          this.cursor -= 1;
          this.scrollToIndex(this.cursor);
        } else if (this.cursor === 1) {
          this.cursor = this.suggestions.length;
          this.scrollToIndex(this.cursor);
        }
      }
    },
    keyDown() {
      if (this.displaySuggestions) {
        event.preventDefault();
        if (this.cursor < this.suggestions.length) {
          this.cursor += 1;
          this.scrollToIndex(this.cursor);
        } else if (this.cursor === this.suggestions.length) {
          this.cursor = 1;
          this.scrollToIndex(this.cursor);
        }
      }
    },
    keyEnter() {
      if (this.displaySuggestions && this.suggestions[this.cursor - 1]) {
        this.selectAddress(this.suggestions[this.cursor - 1]);
        this.displaySuggestions = false;
      }
    },
    keyEscape() {
      this.displaySuggestions = false;
    },
    scrollToIndex(index) {
      const listElement = document.getElementById(`sensis-suggestion-list`);
      const itemElement = document.querySelector(
        `#sensis-suggestion-list .suggestion-item`
      );
      const itemHeight = itemElement.offsetHeight;
      const scrollTop = listElement.scrollTop;
      const viewport = scrollTop + listElement.offsetHeight;
      const scrollOffset = itemHeight * index - itemHeight;

      if (scrollOffset < scrollTop || scrollOffset + itemHeight > viewport) {
        listElement.scrollTo({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    },
    validate() {
      this.hasError = !this.isClean;
      return this.isClean;
    },
  },
};
</script>

<style scoped>
</style>


// WEBPACK FOOTER //
// SensisAutocomplete.vue?290db3ec